import { ProjectCallsPatternType } from "@prisma/client";
import { CSSProperties, ReactNode } from "react";
import { Components } from "@/app/(components)/_components/Components";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import {
  getCategoryEmptyKey,
  getCategoryIcon,
  getCategoryStyle,
} from "@/app/(components)/specifics/tools/CallAnalysis/CallAnalysis.logic";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { ModalStore } from "@/app/stores/ModalStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { AnalysisCategoryLayout, CallAnalysisCategory } from "@/app/types/call/CallAnalysis";
import { CallScopeFilter } from "@/app/types/call/CallScopeFilter";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { InsightCallSource } from "@/app/types/project/InsightCallSource";
import { ProjectInsightItem } from "@/app/types/project/ProjectInsightItem";
import { ProjectPatternItem } from "@/app/types/project/ProjectPatternItem";
import { UStyles } from "@/app/utils/UStyles";
import modalStyles from "../AnalysisInsights/AnalysisInsights.module.css";
import { preloadAnalysisInsights } from "./AnalysisCategory.logic";
import styles from "./AnalysisCategory.module.css";
import InsightItem from "./components/InsightItem/InsightItem";

type Props = DefaultProps & {
  category?: CallAnalysisCategory;
  icon?: ReactNode;
  colors?: { color: string; bg: string };
  iconSize?: number;
  titleKey: TKey;
  subtitleKey: TKey;
  data?: { items: (ProjectInsightItem | ProjectPatternItem)[]; totalCount: number };
  children?: ReactNode;
  layout?: AnalysisCategoryLayout;
  filter?: CallScopeFilter;
  source?: InsightCallSource;
  patternType?: ProjectCallsPatternType;
};

export default function AnalysisCategory(props: Props) {
  const T = useT();
  const {
    params,
    category,
    icon,
    colors,
    iconSize,
    titleKey,
    subtitleKey,
    data,
    children,
    layout = AnalysisCategoryLayout.STACKED,
    filter,
    source = InsightCallSource.INSIGHT,
    patternType,
  } = props;
  const { lang } = params;
  const user = useAuthUser();

  const cardColors = category
    ? getCategoryStyle(category)
    : colors || { color: "var(--color-green-3)", bg: "var(--color-green-4)" };
  const cardIcon = category ? getCategoryIcon(category) : icon;

  const hasData = data && data.items.length > 0;
  const displayedInsights = data?.items || [];
  const hasMore = data ? data.totalCount > 3 : false;
  const canSeeMore = hasMore && (!!category || !!patternType);

  const currentProject = ProjectStore((state) => state.currentProject);

  const isDataDriven = !!category || !!patternType;

  const handleSeeMore = () => {
    if (!currentProject || !canSeeMore) return;
    const emptyKey = category ? getCategoryEmptyKey(category) : TKey.CALL_ANALYSIS_PATTERNS_EMPTY;
    const headerTitleJsx = (
      <div
        className={modalStyles.headerInfo}
        style={
          {
            "--category-color": cardColors.color,
            "--category-color-bg": cardColors.bg,
          } as CSSProperties
        }
      >
        <div className={modalStyles.iconWrapper}>{cardIcon}</div>
        <div className={modalStyles.texts}>
          <h4 className={modalStyles.subtitle}>{T(titleKey)}</h4>
        </div>
      </div>
    );

    ModalStore.getState().openModal(
      Components.AnalysisInsights,
      headerTitleJsx,
      {
        params,
        projectId: currentProject.id,
        source,
        category,
        patternType,
        titleKey,
        colors: cardColors,
        emptyKey,
        filter,
      },
      ModalType.LARGE,
    );
  };

  if (user && isDataDriven && !hasData) {
    return null;
  }

  return (
    <div
      className={styles.card}
      style={
        {
          "--card-color": cardColors.color,
          "--card-color-bg": cardColors.bg,
          "--icon-size": iconSize ? `${iconSize}px` : "24px",
        } as CSSProperties
      }
    >
      <div className={styles.header}>
        <div className={styles.iconWrapper}>{cardIcon}</div>

        {layout === AnalysisCategoryLayout.STACKED && user && (
          <h3 className={styles.headerTitle}>
            {T(titleKey)}
            {data && data.totalCount > 0 && (
              <span
                className={UStyles.classNames(styles.badge, canSeeMore && styles.badgeClickable)}
                onClick={canSeeMore ? handleSeeMore : undefined}
                onMouseEnter={canSeeMore ? preloadAnalysisInsights : undefined}
              >
                ({data.totalCount})
              </span>
            )}
          </h3>
        )}

        {layout === AnalysisCategoryLayout.SIDE && (
          <div className={styles.texts}>
            <h3 className={styles.title}>{T(titleKey)}</h3>
            <p className={styles.subtitle}>{T(subtitleKey)}</p>
          </div>
        )}
      </div>

      {layout === AnalysisCategoryLayout.STACKED && !user && (
        <div className={styles.texts}>
          <h3 className={styles.title}>{T(titleKey)}</h3>
          <p className={styles.subtitle}>{T(subtitleKey)}</p>
        </div>
      )}

      {children}

      {user && hasData && (
        <>
          <div className={styles.list}>
            {displayedInsights.map((insight) => (
              <InsightItem
                key={insight.id}
                insight={insight}
                lang={lang}
                params={params}
                source={source}
                filter={filter}
              />
            ))}
          </div>
          {canSeeMore && (
            <div className={styles.actionWrapper}>
              <button
                className={styles.actionLink}
                onClick={handleSeeMore}
                onMouseEnter={preloadAnalysisInsights}
              >
                {T(TKey.CALL_ANALYSIS_SEE_MORE)}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
